import React, {useEffect, useMemo} from 'react'
import styles from './UIGamePlayerCardInventory.module.scss'
import clsx from 'clsx'
import {CARD_POPUP_BODY, cardEnumsToCardState} from '../../utils/cards'
import {UIGameCardInventory} from '../../common/UIGameCardInventory/UIGameCardInventory'
import {useGameStore} from '../../store/GameStoreContext'
import {CardEnum} from '@colonist/game-states'
import {ArrayUtils} from '@colonist/utils'
import {TradeErrorHighlightLocationEnum, Point} from '@colonist/game-mechanic-validators'
import {TranslationObject} from '@colonist/i18n'
import {getCardsToGreyForBankTrade, removeCardsSelected, getCanClickCardDataForAllCards} from './utils'
import {usePlayerValidators} from '../../store/selectors/usePlayerValidators'
import {useIsReplay} from '../../store/selectors/useIsReplay'
import {GameAction, ModeSettingEnum} from '@colonist/game-base-types'
import {CardClickPopupType, CardDataController, CardPopupInformation} from '@colonist/game-card-types'
import {useUIGameSocketContext} from '../../contexts/UIGameSocketContext'
import {useCurrentStateValidator} from '../../store/selectors/useCurrentStateValidator'

/**
 * Gets the HTML position of a card in the player's card inventory
 * @param cardEnum - The card to find the position for
 * @returns Point with coordinates relative to the ui-game container, or undefined if not found
 */
export function getPointPositionFromPlayerInventory(cardEnum: CardEnum): Point | undefined {
    const playerInventory = document.getElementById('player-card-inventory') as HTMLElement | null
    if(!playerInventory) return undefined

    const uiGameContainer = document.getElementById('ui-game') as HTMLElement | null
    if(!uiGameContainer) return undefined

    const uiGameRect = uiGameContainer.getBoundingClientRect()

    const cardElement = playerInventory.querySelector(`[data-card-enum="${cardEnum}"]`) as HTMLElement | null
    if(!cardElement) {
        const inventoryRect = playerInventory.getBoundingClientRect()
        const inventoryCenterX = inventoryRect.left - uiGameRect.left
        const inventoryTopY = inventoryRect.top - uiGameRect.top

        return new Point(inventoryCenterX, inventoryTopY)
    }

    const cardRect = cardElement.getBoundingClientRect()
    const cardCenterX = cardRect.left + cardRect.width / 2 - uiGameRect.left
    const cardTopY = cardRect.top - uiGameRect.top
    return new Point(cardCenterX, cardTopY)
}

/**
 * Gets the dimensions of a card from the player's card inventory
 * @returns Object with width and height of a card
 */
export function getCardDimensionsFromPlayerInventory(): {width: number, height: number} {
    const playerInventory = document.getElementById('player-card-inventory') as HTMLElement | null
    if(!playerInventory) return {width: 0, height: 0}

    const cardElement = playerInventory.querySelector('[data-card-enum]') as HTMLElement | null

    if(!cardElement) return {width: 0, height: 0}

    const cardRect = cardElement.getBoundingClientRect()
    return {
        width: cardRect.width,
        height: cardRect.height,
    }
}

export interface UIGamePlayerCardInventoryProps {
    className?: string | undefined
    background?: 'beige' | 'transparent'
    disableTooltip?: boolean | undefined
    disableCardScaleOnHover?: boolean | undefined
}

export const UIGamePlayerCardInventory = ({
    className,
    background = 'beige',
    disableTooltip = false,
    disableCardScaleOnHover = false,
}: UIGamePlayerCardInventoryProps) => {
    const setGameCardInventory = useGameStore((state) => state.gameCardInventory.setCardInventory)
    const selectedCards = useGameStore((state) => state.gameCardInventory.selectedCards)
    const cardsAnimating = useGameStore((state) => state.gameCardAnimator.cardsAnimating)
    const cardsAnimatingToMyHand = useMemo(() =>
        cardsAnimating.filter((card) => card.isAnimatingToMyHand).map((card) => card.card.cardEnum),
    [cardsAnimating])
    const cardsToGreyOutWhenPickingForActionBox = useGameStore((state) => state.gameCardInventory.cardsToGreyOut)
    const onSelectCardForTrade = useGameStore((state) => state.gameCardInventory.onSelectCardForTrade)
    const onSelectCardForActionBox = useGameStore((state) => state.gameCardInventory.onSelectCardForActionBox)
    const actionBoxType = useGameStore((state) => state.actionBox.actionBoxData?.type)
    const gameState = useGameStore((state) => state.gameState)
    const {getPlayerValidator} = usePlayerValidators()
    const myColor = useGameStore((state) => state.myColor)
    const isBankTradeMode = useGameStore((state) => state.gameTrade.isBankTradeMode)
    const isShowTradePanel = useGameStore((state) => state.gameTrade.isShowTradePanel)
    const tradeError = useGameStore((state) => state.gameTrade.tradeError)
    const translationKeyContext = useGameStore((state) => state.gameMode.translationKeyContext)
    const beginnerActionState = useGameStore((state) => state.gameBeginnerHint.beginnerActionState)
    const beginnerHintData = useGameStore((state) => state.gameBeginnerHint.beginnerHintData)
    const modeSetting = useGameStore((state) => state.gameSettings.modeSetting)
    const isReplay = useIsReplay()
    const {sendClickedDevelopmentCard, sendRequestActionSwap, sendConfirmUseDevelopmentCard} = useUIGameSocketContext()
    const currentStateValidator = useCurrentStateValidator()

    const isBeginnerMode = modeSetting == ModeSettingEnum.BeginnerModeClassic4P
    const offeredResourcesSuggestion: CardEnum[] = beginnerHintData?.data.offeredResources != null ? beginnerHintData?.data.offeredResources : []
    const shouldShowSuggestion = offeredResourcesSuggestion.length > 0 && isShowTradePanel
    const myPlayerValidator = getPlayerValidator(myColor)
    const myBankTradeRatioState = myPlayerValidator.bankTradeRatiosState
    const isActionBoxPickCardOpening = actionBoxType === 'pickCard'

    const myResourceCards = myPlayerValidator.resourceCards.cards
    const myDevelopmentCards = gameState.mechanicDevelopmentCardsState?.players?.[myColor]?.developmentCards.cards ?? []
    const myProgressCards = gameState.mechanicProgressCardsState?.players?.[myColor]?.progressCards.cards ?? []
    const anyResourceCards = isShowTradePanel && !isBankTradeMode && !isBeginnerMode ? [CardEnum.AnyResource] : []

    const allCardsInInventory = useMemo(() => [
        ...myResourceCards,
        ...myDevelopmentCards,
        ...myProgressCards,
        ...anyResourceCards,
    ], [myResourceCards, myDevelopmentCards, myProgressCards, anyResourceCards])

    const allVisibleCards = useMemo(() => {
        const cardsToRemove = [...cardsAnimatingToMyHand, ...selectedCards]
        return removeCardsSelected(allCardsInInventory, cardsToRemove)
    }, [allCardsInInventory, cardsAnimatingToMyHand, selectedCards])

    useEffect(() => {
        setGameCardInventory(allCardsInInventory)
    }, [allCardsInInventory])

    const cardState = useMemo(() => cardEnumsToCardState(allVisibleCards), [allVisibleCards])

    const cardsToGreyOut = useMemo(() => {
        if(cardsToGreyOutWhenPickingForActionBox != undefined && isActionBoxPickCardOpening) return cardsToGreyOutWhenPickingForActionBox
        let cardsToGrey: CardEnum[] = []

        if(isShowTradePanel) {
            cardsToGrey = [...myDevelopmentCards, ...myProgressCards]
        }

        if(isShowTradePanel && isBankTradeMode) {
            cardsToGrey.push(...getCardsToGreyForBankTrade(myBankTradeRatioState, cardState))
        }

        return cardEnumsToCardState(cardsToGrey)
    }, [
        myDevelopmentCards,
        myProgressCards,
        myBankTradeRatioState,
        cardsToGreyOutWhenPickingForActionBox,
        isShowTradePanel,
        isBankTradeMode,
        cardState,
        isActionBoxPickCardOpening,
    ])

    const cardsToHighlight = useMemo(() => {
        const tradeErrorHighlightCards = tradeError?.payload?.highlightCards?.cardsToHighlight ?? []
        const tradeErrorHighlightLocations = tradeError?.payload?.highlightCards?.locations ?? []
        if(tradeErrorHighlightLocations.includes(TradeErrorHighlightLocationEnum.OfferedSelector)) {
            return tradeErrorHighlightCards
        }
        return []
    }, [tradeError])

    const cardsToScaleOnHover = useMemo(() => {
        if(isShowTradePanel || disableCardScaleOnHover) return []
        const cardsToScale = [...myProgressCards]
        ArrayUtils.getUniqueElements(cardsToScale)
        return cardsToScale
    }, [myProgressCards, isShowTradePanel, disableCardScaleOnHover])

    const canClickCardDataForAllCards = useMemo(() => getCanClickCardDataForAllCards({
        allMyCards: allVisibleCards,
        currentState: gameState.currentState,
        isReplay: isReplay,
        myColor: myColor,
        translationKeyContext: translationKeyContext,
        isShowTradePanel: isShowTradePanel,
        isActionBoxPickCardOpening: isActionBoxPickCardOpening,
        currentStateValidator: currentStateValidator,
    }), [
        allVisibleCards,
        gameState.currentState,
        isReplay,
        myColor,
        translationKeyContext,
        isShowTradePanel,
        isActionBoxPickCardOpening,
        currentStateValidator,
    ])

    const cardsHelperText = useMemo(() => {
        const cardsHelperTextLocal: {[key in CardEnum]?: TranslationObject} = {}
        for(const cardEnum in canClickCardDataForAllCards) {
            cardsHelperTextLocal[cardEnum] = canClickCardDataForAllCards[cardEnum].helperText
        }
        return cardsHelperTextLocal
    }, [canClickCardDataForAllCards])

    const cardsToShowTooltip = useMemo(() => {
        if(!isActionBoxPickCardOpening) return undefined

        const progressCardsNotGreyedOut = myProgressCards.filter((cardEnum) => {
            const greyedOutCount = cardsToGreyOut[cardEnum] ?? 0
            const totalCount = cardState[cardEnum] ?? 0
            return greyedOutCount < totalCount
        })

        return progressCardsNotGreyedOut
    }, [isActionBoxPickCardOpening, myProgressCards, cardsToGreyOut, cardState])

    const handleCreateCardConfirmationPopup = (cardEnum: CardEnum) => {
        sendRequestActionSwap(GameAction.ClickedDevelopmentCard)
        const popupInformation: CardPopupInformation = {
            cardEnum: cardEnum,
            cardBody: CARD_POPUP_BODY[cardEnum],
        }
        sendConfirmUseDevelopmentCard(popupInformation)
    }

    const handleClickCard = (cardEnum: CardEnum) => {
        const isNotBeginnerGameActionClickedDevCard = beginnerActionState != null && beginnerActionState?.type != GameAction.ClickedDevelopmentCard
        const isNotBeginnerGameActionTrade = beginnerActionState != null && beginnerActionState?.type != GameAction.CreateTrade
        if(actionBoxType === 'pickCard') {
            onSelectCardForActionBox?.(cardEnum)
            return
        }

        if(isShowTradePanel) {
            if(isNotBeginnerGameActionTrade) {
                return
            }
            onSelectCardForTrade?.(cardEnum)
            return
        }

        const canClickCardData = canClickCardDataForAllCards[cardEnum]
        if(!canClickCardData?.canClickCard) return

        const cardData = CardDataController.getCardDataForCard(cardEnum)

        if(cardData?.clickPopupType == CardClickPopupType.NormalClick) {
            if(isNotBeginnerGameActionClickedDevCard) {
                return
            }
            sendClickedDevelopmentCard(cardEnum)
            return
        }
        if(cardData?.clickPopupType == CardClickPopupType.TradeWindow) {
            if(isNotBeginnerGameActionTrade) {
                return
            }
            onSelectCardForTrade?.(cardEnum)
            return
        }
        if(cardData?.clickPopupType == CardClickPopupType.ConfirmationPopup) {
            if(isNotBeginnerGameActionClickedDevCard) {
                return
            }
            handleCreateCardConfirmationPopup(cardEnum)
            return
        }
    }

    const shouldShowBankTradeRatio = isBankTradeMode && isShowTradePanel
    const tooltipEnabled = !disableTooltip && !isShowTradePanel
    return (
        <div className={clsx(styles.container, className, styles[background])}>
            <UIGameCardInventory
                onClickCard={handleClickCard}
                cardState={cardState}
                className={styles.inventory}
                showTipTitle={tooltipEnabled}
                showTipBody={tooltipEnabled}
                cardsToGreyOut={cardsToGreyOut}
                cardsToHighlight={cardsToHighlight}
                cardsToSuggestion={shouldShowSuggestion ? offeredResourcesSuggestion : []}
                cardsToScaleOnHover={cardsToScaleOnHover}
                cardsHelperText={cardsHelperText}
                bankTradeRatiosState={shouldShowBankTradeRatio ? myBankTradeRatioState : undefined}
                cardsToShowTooltip={cardsToShowTooltip}
                id="player-card-inventory"/>
        </div>
    )
}
